import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { AuthShell, Field } from "./Login";
import GlobalLocationSearch from "@/components/GlobalLocationSearch";
import { Sparkles, ArrowUpRight, Briefcase, MapPin } from "lucide-react";

const NETWORK_ROLES = [
  ["mentor", "Mentor"],
  ["recruteur", "Recruteur / Recruteuse"],
  ["expert", "Expert(e) sectoriel(le)"],
  ["entrepreneur", "Entrepreneur(e)"],
  ["investisseur", "Investisseur / Investisseuse"],
  ["conseiller", "Conseiller(ère)"],
  ["leader_communautaire", "Leader communautaire"],
  ["ancien_etudiant", "Ancien(ne) étudiant(e)"],
  ["professionnel", "Autre professionnel(le)"],
];

/**
 * ProfessionalRegister — inscription publique pour mentors, recruteurs,
 * experts, entrepreneurs, investisseurs, conseillers…
 *
 * Flow : /auth/signup (compte user) → /network/join (profil réseau public).
 */
export default function ProfessionalRegister() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm: "",
    display_name: "", role_network: "mentor",
    headline_fr: "", bio_fr: "",
    expertises: "", sectors: "",
    languages: "fr", city: "", country: "",
    availability: "medium",
    // Vetting fields (P1)
    organization: "", function_title: "",
    linkedin_url: "", website_url: "",
    join_reason_fr: "",
    accept_terms: false,
  });

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
    className: "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm text-[#1F2937]",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Mot de passe : min. 8 caractères"); return; }
    if (form.password !== form.confirm) { toast.error("Mots de passe différents"); return; }
    if (!form.accept_terms) { toast.error("Vous devez accepter les conditions"); return; }
    if (!form.headline_fr || form.headline_fr.length < 6) { toast.error("Intitulé professionnel requis (min. 6 caractères)"); return; }
    if (!form.join_reason_fr || form.join_reason_fr.length < 20) {
      toast.error("Merci d'expliquer votre motivation (min. 20 caractères)"); return;
    }

    setBusy(true);
    try {
      // Step 1 — user signup
      const signupRes = await api.post("/auth/signup", {
        email: form.email,
        password: form.password,
        password_confirm: form.confirm,
        first_name: form.first_name,
        last_name: form.last_name,
        name: `${form.first_name} ${form.last_name}`.trim(),
        lang,
        accept_terms: true,
      });
      localStorage.setItem("altiora_token", signupRes.data.access_token);

      // Step 2 — network profile
      try {
        await api.post("/network/join", {
          display_name: form.display_name || `${form.first_name} ${form.last_name}`.trim(),
          role_network: form.role_network,
          headline_fr: form.headline_fr,
          bio_fr: form.bio_fr,
          expertises: form.expertises.split(",").map((s) => s.trim()).filter(Boolean),
          sectors: form.sectors.split(",").map((s) => s.trim()).filter(Boolean),
          languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
          city: form.city || null,
          country: form.country || null,
          availability: form.availability,
          open_to: [],
          organization: form.organization || null,
          function_title: form.function_title || null,
          linkedin_url: form.linkedin_url || null,
          website_url: form.website_url || null,
          join_reason_fr: form.join_reason_fr,
        });
      } catch (netErr) {
        // Le compte a été créé; on log et on alerte l'utilisateur.
        console.warn("Network join failed:", netErr?.response?.data?.detail);
        toast.warning(
          "Compte créé, mais nous n'avons pas pu enregistrer votre profil réseau. Vous pourrez le compléter depuis /app/network.",
          { duration: 8000 }
        );
      }

      toast.success("Compte créé. Votre profil est en validation — vous recevrez un courriel dès qu'il sera approuvé (24-48 h).", {
        duration: 10000,
      });
      await new Promise((r) => setTimeout(r, 300));
      window.location.href = "/app/mentor";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Inscription échouée");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#0B2545]">ALTIORA · Espace Professionnel</div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
        Rejoindre en tant que professionnel
      </h1>
      <p className="mt-3 text-sm text-[#5B5B62] leading-relaxed">
        Mentors, recruteurs, experts, entrepreneurs, conseillers : rendez votre expertise visible dans
        l&apos;écosystème ALTIORA et connectez-vous aux personnes dont la trajectoire s&apos;aligne avec la vôtre.
      </p>

      <div className="mt-5 rounded-md border border-[#C5A880]/50 bg-[#FBF6EA] px-4 py-3 flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-[#7A5B14] mt-0.5 flex-shrink-0" strokeWidth={2} />
        <div className="text-xs text-[#7A5B14] leading-relaxed">
          Inscription gratuite. Votre profil apparaîtra dans l&apos;écosystème après validation manuelle
          par notre équipe (24-48 h) pour préserver la qualité du réseau.
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2545]">Identité</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom *"><input data-testid="pro-signup-firstname" required {...bind("first_name")} /></Field>
          <Field label="Nom *"><input data-testid="pro-signup-lastname" required {...bind("last_name")} /></Field>
        </div>
        <Field label="Courriel *"><input data-testid="pro-signup-email" type="email" required {...bind("email")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mot de passe *"><input data-testid="pro-signup-password" type="password" minLength={8} required {...bind("password")} /></Field>
          <Field label="Confirmation *"><input data-testid="pro-signup-confirm" type="password" minLength={8} required {...bind("confirm")} /></Field>
        </div>

        <div className="pt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2545]">Profil professionnel</div>
        <Field label="Rôle dans le réseau *">
          <select data-testid="pro-signup-role" {...bind("role_network")}>
            {NETWORK_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Intitulé professionnel * (visible publiquement)">
          <input data-testid="pro-signup-headline" required minLength={6} placeholder="Ex : Analyste SOC senior · 12 ans · Montréal" {...bind("headline_fr")} />
        </Field>
        <Field label="Bio courte">
          <textarea data-testid="pro-signup-bio" rows={3} placeholder="Votre parcours en 2-3 phrases…" {...bind("bio_fr")} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expertises (virgules)">
            <input data-testid="pro-signup-expertises" placeholder="SIEM, EDR, Python" {...bind("expertises")} />
          </Field>
          <Field label="Secteurs (virgules)">
            <input data-testid="pro-signup-sectors" placeholder="cybersecurite, finance" {...bind("sectors")} />
          </Field>
        </div>
        <Field label="Où intervenez-vous ?">
          <GlobalLocationSearch
            lang="fr"
            placeholder="Ville — Casablanca, Montréal, Bruxelles, Dakar…"
            onSelect={({ city, country }) => {
              setForm((f) => ({
                ...f,
                city: city || f.city,
                country: country || f.country,
              }));
            }}
          />
          {(form.city || form.country) && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#F1EFE9] border border-[#E4E1D8] px-2.5 py-1 text-xs text-navy" data-testid="pro-signup-location-selected">
              <MapPin className="h-3 w-3" />
              <span className="font-medium">{[form.city, form.country].filter(Boolean).join(", ")}</span>
            </div>
          )}
          {/* Hidden fields keep the same names/testids for backend compat & tests */}
          <input type="hidden" data-testid="pro-signup-city" value={form.city || ""} readOnly />
          <input type="hidden" data-testid="pro-signup-country" value={form.country || ""} readOnly />
        </Field>

        <div className="pt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2545]">Vérification (soumis à validation)</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Organisation / Entreprise">
            <input data-testid="pro-signup-organization" placeholder="Ex : Banque XYZ, Cabinet ABC" {...bind("organization")} />
          </Field>
          <Field label="Fonction / Titre">
            <input data-testid="pro-signup-function" placeholder="Ex : Directeur cybersécurité" {...bind("function_title")} />
          </Field>
        </div>
        <Field label="LinkedIn (recommandé)">
          <input data-testid="pro-signup-linkedin" type="url" placeholder="https://www.linkedin.com/in/…" {...bind("linkedin_url")} />
        </Field>
        <Field label="Site web (si disponible)">
          <input data-testid="pro-signup-website" type="url" placeholder="https://…" {...bind("website_url")} />
        </Field>
        <Field label="Raison de rejoindre ALTIORA * (min. 20 caractères)">
          <textarea
            data-testid="pro-signup-reason"
            rows={4}
            required
            minLength={20}
            placeholder="Ex : Je souhaite mentorer 2-3 analystes junior par mois vers des postes en blue team…"
            {...bind("join_reason_fr")}
          />
        </Field>

        <label className="flex items-start gap-2 text-xs text-[#1F2937] pt-2">
          <input
            type="checkbox"
            data-testid="pro-signup-terms"
            checked={form.accept_terms}
            onChange={(e) => setForm((f) => ({ ...f, accept_terms: e.target.checked }))}
            className="mt-0.5"
          />
          <span>
            J&apos;accepte les <a href="/terms" target="_blank" className="text-[#0B2545] underline">conditions</a>
            {" "}et la <a href="/privacy" target="_blank" className="text-[#0B2545] underline">politique de confidentialité</a>.
          </span>
        </label>

        <button
          data-testid="pro-signup-submit"
          type="submit" disabled={busy}
          className="w-full mt-3 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white font-medium py-3 transition-colors flex items-center justify-center gap-2"
        >
          <Briefcase className="h-4 w-4" strokeWidth={2} />
          {busy ? "Création en cours…" : "Créer mon compte professionnel"}
        </button>
      </form>

      <div className="mt-8 pt-6 border-t border-[#E4E1D8] space-y-2 text-xs text-[#5B5B62] text-center">
        <div>
          Vous cherchez à faire progresser votre carrière ?{" "}
          <Link to="/register" className="text-[#0B2545] hover:text-[#DCC29A] inline-flex items-center gap-0.5">
            Créer un compte membre <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div>
          Vous représentez un organisme, une entreprise ou une institution ?{" "}
          <Link to="/partner/register" className="text-[#0B2545] hover:text-[#DCC29A] inline-flex items-center gap-0.5">
            Devenir partenaire <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div>
          Déjà inscrit ?{" "}
          <Link to="/login" className="text-[#0B2545] hover:text-[#DCC29A] font-medium">Se connecter</Link>
        </div>
      </div>
    </AuthShell>
  );
}
